/**
 * `/q/:token` — the homeowner's quote and job-completion page, opened from an
 * email (contractor pages phase 3). Public, mobile-first, no navigation: the
 * token is the credential, mounted like `/l/:token` (pages/LeadBrief.tsx).
 *
 * Two kinds of link share the page:
 *  - `quote`: the contractor's price, what it covers, line items and terms;
 *    Accept (type your name + tick "I accept this quote from … for …") or
 *    Decline (optional reason);
 *  - `completion`: "Did {contractor} complete the job?" — Confirm, or
 *    "Something's wrong" with a reason (required), which opens a dispute.
 *
 * Opening it never changes anything (`GET /q/:token` is render-only, so an
 * email scanner prefetching the link cannot accept a quote). Every answer is
 * an explicit POST; the first wins on the server, and invalid, expired and
 * already-used links all read calmly. Nothing about the homeowner is shown:
 * the page carries the contractor, the quote and the job, never the contact.
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ApiError } from '../api/errors';
import type { PublicQuote, PublicQuoteContractor, PublicQuoteFailReason, PublicQuoteProject, PublicQuoteResponse } from '../api/types';
import { useSession } from '../auth/session';
import { formatCents, lineItemTotalCents } from '../leads/feeMath';
import { formatPhone } from '../leads/format';
import { ErrorNotice, Loading, formatDate } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

export const CONSUMER_PRIVACY_URL = 'https://app.finderlyfix.com/PrivacyPolicy';
export const CONSUMER_TERMS_URL = 'https://app.finderlyfix.com/TermsOfService';

const FAIL_REASONS: readonly PublicQuoteFailReason[] = ['not_found', 'expired', 'used'];

/** One calm sentence pair per way a link can stop working. */
const FAIL_COPY: Record<PublicQuoteFailReason, { title: string; body: string }> = {
  not_found: {
    title: 'This link is no longer valid',
    body: 'It may have been replaced by a newer quote. If you’re expecting one, contact the contractor who sent it.',
  },
  expired: {
    title: 'This link has expired',
    body: 'Quote links only stay open for a while. If you’d still like the work done, ask the contractor for a fresh quote.',
  },
  used: {
    title: 'This link was already used',
    body: 'Someone already responded with it, so there’s nothing more to do here.',
  },
};

const NUDGE = {
  name: 'Type your name to accept the quote.',
  tick: 'Tick the box to accept the quote.',
  reason: 'Tell us what’s wrong so we can look into it.',
};

function telHref(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  return digits.length === 10 ? `tel:+1${digits}` : `tel:${raw.replace(/[^\d+]/g, '')}`;
}

function failReasonOf(value: unknown): PublicQuoteFailReason | null {
  return typeof value === 'string' && (FAIL_REASONS as readonly string[]).includes(value) ? (value as PublicQuoteFailReason) : null;
}

/** A non-2xx that still carries the `{ ok:false, reason }` body reads as that reason. */
function failFromError(error: ApiError | null): PublicQuoteFailReason | null {
  if (!error) return null;
  const reason = failReasonOf(error.body.reason);
  if (reason) return reason;
  return error.status === 404 || error.status === 410 ? 'not_found' : null;
}

type Loaded = Extract<PublicQuoteResponse, { ok: true }>;

function isLoaded(data: PublicQuoteResponse | null): data is Loaded {
  if (!data || data.ok !== true) return false;
  const d = data as Partial<Loaded>;
  return !!d.quote && !!d.contractor && (d.kind === 'quote' || d.kind === 'completion');
}

export function HomeownerQuote({ token }: { token: string }) {
  const { api } = useSession();
  const page = useQuery(() => api.getHomeownerQuote(token), [token]);
  // What this visit changed; the server's answer is the truth, this only
  // renders it without a second GET.
  const [quoteStatus, setQuoteStatus] = useState<PublicQuote['status'] | null>(null);
  const [project, setProject] = useState<PublicQuoteProject | null>(null);
  const [failed, setFailed] = useState<PublicQuoteFailReason | null>(null);
  const [thanks, setThanks] = useState<string | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const viewKey = `${page.loading}|${quoteStatus}|${project?.status}|${failed}`;
  const firstView = useRef(true);
  useEffect(() => {
    // Move focus to the new heading when an answer changes the page (not on first load).
    if (page.loading) return;
    if (firstView.current) {
      firstView.current = false;
      return;
    }
    titleRef.current?.focus();
  }, [viewKey, page.loading]);

  const data = page.data;
  const loadFail = failFromError(page.error) ?? (data && data.ok === false ? (failReasonOf(data.reason) ?? 'not_found') : null);
  const loaded = isLoaded(data) ? data : null;

  if (page.loading) {
    return (
      <main className="brief" id="main">
        <div className="eyebrow">FinderlyFix · your quote</div>
        <Loading label="Loading your quote" />
      </main>
    );
  }
  if (page.error && !loadFail) {
    return (
      <main className="brief" id="main">
        <div className="eyebrow">FinderlyFix · your quote</div>
        <h1 id="page-title" tabIndex={-1} ref={titleRef}>
          We couldn’t load this page
        </h1>
        <ErrorNotice error={page.error} />
        <p className="muted">Check your connection and try again in a moment.</p>
        <Footer contractor={null} />
      </main>
    );
  }
  const reason = failed ?? loadFail ?? (loaded ? null : 'not_found');
  if (reason || !loaded) {
    return <FailView reason={reason ?? 'not_found'} titleRef={titleRef} contractor={loaded?.contractor ?? null} />;
  }

  const contractor = loaded.contractor;
  const quote: PublicQuote = quoteStatus ? { ...loaded.quote, status: quoteStatus } : loaded.quote;
  const proj = project ?? loaded.project;

  return (
    <main className="brief" id="main">
      <div className="eyebrow">FinderlyFix · {loaded.kind === 'completion' ? 'job completion' : 'your quote'}</div>
      <ContractorHeader contractor={contractor} />
      {thanks ? (
        <div className="notice ok" role="status">
          {thanks}
        </div>
      ) : null}
      {loaded.kind === 'quote' ? (
        <QuoteView
          token={token}
          quote={quote}
          contractor={contractor}
          titleRef={titleRef}
          onAnswered={(status, next, message) => {
            setQuoteStatus(status);
            if (next) setProject(next);
            setThanks(message);
          }}
          onFail={setFailed}
        />
      ) : !proj ? (
        <FailBody reason="not_found" titleRef={titleRef} />
      ) : (
        <CompletionView
          token={token}
          quote={quote}
          project={proj}
          contractor={contractor}
          titleRef={titleRef}
          onAnswered={(next, message) => {
            setProject(next);
            setThanks(message);
          }}
          onFail={setFailed}
        />
      )}
      <Footer contractor={contractor} />
    </main>
  );
}

type TitleRef = RefObject<HTMLHeadingElement | null>;

function Title({ titleRef, children }: { titleRef: TitleRef; children: ReactNode }) {
  return (
    <h1 id="page-title" tabIndex={-1} ref={titleRef}>
      {children}
    </h1>
  );
}

function FailBody({ reason, titleRef }: { reason: PublicQuoteFailReason; titleRef: TitleRef }) {
  return (
    <>
      <Title titleRef={titleRef}>{FAIL_COPY[reason].title}</Title>
      <p className="muted">{FAIL_COPY[reason].body}</p>
    </>
  );
}

function FailView({ reason, titleRef, contractor }: { reason: PublicQuoteFailReason; titleRef: TitleRef; contractor: PublicQuoteContractor | null }) {
  return (
    <main className="brief" id="main">
      <div className="eyebrow">FinderlyFix · your quote</div>
      {contractor ? <ContractorHeader contractor={contractor} /> : null}
      <FailBody reason={reason} titleRef={titleRef} />
      <Footer contractor={contractor} />
    </main>
  );
}

function ContractorHeader({ contractor }: { contractor: PublicQuoteContractor }) {
  return (
    <header className="row" style={{ gap: 12, alignItems: 'center' }}>
      {contractor.logo_url ? (
        <img
          src={contractor.logo_url}
          alt=""
          width={48}
          height={48}
          style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8, flex: 'none' }}
        />
      ) : null}
      <div className="stack-xs" style={{ minWidth: 0 }}>
        <strong style={{ fontSize: 17 }}>{contractor.name}</strong>
        {contractor.phone ? (
          <a href={telHref(contractor.phone)}>
            Call {contractor.name} · {formatPhone(contractor.phone)}
          </a>
        ) : null}
      </div>
    </header>
  );
}

function Footer({ contractor }: { contractor: PublicQuoteContractor | null }) {
  return (
    <footer className="share-foot small muted">
      <p style={{ margin: '0 0 4px' }}>{contractor ? `Sent by FinderlyFix on behalf of ${contractor.name}.` : 'Sent by FinderlyFix.'}</p>
      <p style={{ margin: 0 }}>
        <a href={CONSUMER_PRIVACY_URL}>Privacy</a> · <a href={CONSUMER_TERMS_URL}>Terms</a>
      </p>
    </footer>
  );
}

function money(quote: PublicQuote, cents: number): string {
  return formatCents(cents, { currency: quote.currency || 'USD' });
}

function QuoteDetails({ quote }: { quote: PublicQuote }) {
  const lines = quote.line_items ?? [];
  return (
    <>
      <section className="panel panel-pad stack-sm" aria-labelledby="quote-total">
        <h2 id="quote-total" className="small muted" style={{ margin: 0 }}>
          Quote total
        </h2>
        <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: 'var(--ink)' }}>{money(quote, quote.amount_cents)}</p>
        {quote.valid_until && quote.status === 'sent' ? <p className="small muted" style={{ margin: 0 }}>Valid until {formatDate(quote.valid_until)}</p> : null}
      </section>
      <section className="stack-sm" aria-labelledby="quote-scope">
        <h2 id="quote-scope">What’s included</h2>
        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{quote.scope}</p>
      </section>
      {lines.length ? (
        <section className="stack-sm" aria-labelledby="quote-lines">
          <h2 id="quote-lines">Breakdown</h2>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col" className="num">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((item, i) => (
                  <tr key={i}>
                    <td>
                      {item.description}
                      {typeof item.qty === 'number' && typeof item.unit_cents === 'number' && item.qty !== 1 ? (
                        <span className="small muted">
                          {' '}
                          · {item.qty} × {money(quote, item.unit_cents)}
                        </span>
                      ) : null}
                    </td>
                    <td className="num">{money(quote, lineItemTotalCents(item))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  <td className="num">
                    <strong>{money(quote, quote.amount_cents)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      ) : null}
      {quote.terms ? (
        <section className="stack-sm" aria-labelledby="quote-terms">
          <h2 id="quote-terms">Terms</h2>
          <p className="small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            {quote.terms}
          </p>
        </section>
      ) : null}
    </>
  );
}

function isPastValidity(quote: PublicQuote): boolean {
  if (!quote.valid_until) return false;
  const t = new Date(quote.valid_until).getTime();
  return Number.isFinite(t) && t <= Date.now();
}

function QuoteView({
  token,
  quote,
  contractor,
  titleRef,
  onAnswered,
  onFail,
}: {
  token: string;
  quote: PublicQuote;
  contractor: PublicQuoteContractor;
  titleRef: TitleRef;
  onAnswered: (status: PublicQuote['status'], project: PublicQuoteProject | null, message: string) => void;
  onFail: (reason: PublicQuoteFailReason) => void;
}) {
  const { api } = useSession();
  const [name, setName] = useState('');
  const [ticked, setTicked] = useState(false);
  const [nudge, setNudge] = useState<'name' | 'tick' | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  const tickRef = useRef<HTMLInputElement>(null);
  const amount = money(quote, quote.amount_cents);

  const accept = useMutation(async () => {
    const res = await api.acceptHomeownerQuote(token, name.trim());
    if (res.ok) onAnswered('accepted', res.project ?? null, `Thanks — we’ve told ${contractor.name}.`);
    else onFail(failReasonOf(res.reason) ?? 'not_found');
    return res;
  });
  const decline = useMutation(async () => {
    const res = await api.declineHomeownerQuote(token, reason.trim() || undefined);
    if (res.ok) onAnswered('declined', null, `Thanks — we’ve let ${contractor.name} know.`);
    else onFail(failReasonOf(res.reason) ?? 'not_found');
    return res;
  });
  const error = accept.error ?? decline.error;
  const errorReason = failFromError(error);
  useEffect(() => {
    if (errorReason) onFail(errorReason);
  }, [errorReason, onFail]);
  const pending = accept.pending || decline.pending;

  const status = quote.status === 'sent' && isPastValidity(quote) ? 'expired' : quote.status;

  if (status !== 'sent') {
    const copy: Record<Exclude<PublicQuote['status'], 'sent'>, { title: string; body: string }> = {
      accepted: {
        title: 'You accepted this quote',
        body: `You accepted ${contractor.name}’s quote for ${amount}${quote.accepted_at ? ` on ${formatDate(quote.accepted_at)}` : ''}. They’ll be in touch to arrange the work; you pay ${contractor.name} directly.`,
      },
      declined: {
        title: 'You declined this quote',
        body: `If you change your mind, contact ${contractor.name} directly.`,
      },
      expired: {
        title: 'This quote has expired',
        body: `It was open until ${formatDate(quote.valid_until)}. If you’d still like the work done, contact ${contractor.name} for a fresh quote.`,
      },
      withdrawn: {
        title: 'This quote was withdrawn',
        body: `${contractor.name} withdrew this quote. They may send you a revised one.`,
      },
      draft: FAIL_COPY.not_found,
    };
    const c = copy[status];
    return (
      <>
        <Title titleRef={titleRef}>{c.title}</Title>
        <p className="muted">{c.body}</p>
        {status === 'draft' ? null : <QuoteDetails quote={quote} />}
      </>
    );
  }

  function tryAccept() {
    if (!name.trim()) {
      setNudge('name');
      nameRef.current?.focus();
      return;
    }
    if (!ticked) {
      setNudge('tick');
      tickRef.current?.focus();
      return;
    }
    setNudge(null);
    void accept.run();
  }

  return (
    <>
      <Title titleRef={titleRef}>Your quote from {contractor.name}</Title>
      <QuoteDetails quote={quote} />
      {declining ? (
        <section className="panel panel-pad stack" aria-labelledby="decline-title">
          <h2 id="decline-title">Decline this quote</h2>
          <label className="field">
            <span className="label">
              Anything you’d like {contractor.name} to know? <span className="faint" style={{ fontWeight: 500 }}>· optional</span>
            </span>
            <textarea className="input" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          {errorReason ? null : <ErrorNotice error={decline.error} />}
          <div className="actions">
            <button type="button" className="btn btn-primary" disabled={pending} onClick={() => void decline.run()}>
              {decline.pending ? 'Sending…' : 'Decline quote'}
            </button>
            <button type="button" className="btn" disabled={pending} onClick={() => setDeclining(false)}>
              Back
            </button>
          </div>
        </section>
      ) : (
        <section className="panel panel-pad stack" aria-labelledby="accept-title">
          <h2 id="accept-title">Accept this quote</h2>
          <p className="small muted" style={{ margin: 0 }}>
            Accepting tells {contractor.name} to go ahead. You arrange the work and pay {contractor.name} directly.
          </p>
          <label className="field" htmlFor="accept-name">
            <span className="label">Type your full name</span>
            <input
              id="accept-name"
              ref={nameRef}
              className="input input-lg"
              autoComplete="name"
              maxLength={120}
              value={name}
              aria-invalid={nudge === 'name' || undefined}
              aria-describedby={nudge === 'name' ? 'accept-nudge' : undefined}
              onChange={(e) => {
                setName(e.target.value);
                if (nudge === 'name' && e.target.value.trim()) setNudge(null);
              }}
            />
          </label>
          <label className="check" htmlFor="accept-tick">
            <input
              id="accept-tick"
              ref={tickRef}
              type="checkbox"
              checked={ticked}
              aria-invalid={nudge === 'tick' || undefined}
              aria-describedby={nudge === 'tick' ? 'accept-nudge' : undefined}
              onChange={(e) => {
                setTicked(e.target.checked);
                if (e.target.checked && nudge === 'tick') setNudge(null);
              }}
            />
            <span>
              I accept this quote from {contractor.name} for {amount}
            </span>
          </label>
          {nudge ? (
            <p id="accept-nudge" className="notice bad" role="alert" style={{ margin: 0 }}>
              {NUDGE[nudge]}
            </p>
          ) : null}
          {errorReason ? null : <ErrorNotice error={accept.error} />}
          <div className="actions">
            <button type="button" className="btn btn-accent" disabled={pending} onClick={tryAccept}>
              {accept.pending ? 'Sending…' : `Accept quote · ${amount}`}
            </button>
            <button type="button" className="btn btn-ghost" disabled={pending} onClick={() => setDeclining(true)}>
              Decline
            </button>
          </div>
        </section>
      )}
    </>
  );
}

function CompletionView({
  token,
  quote,
  project,
  contractor,
  titleRef,
  onAnswered,
  onFail,
}: {
  token: string;
  quote: PublicQuote;
  project: PublicQuoteProject;
  contractor: PublicQuoteContractor;
  titleRef: TitleRef;
  onAnswered: (project: PublicQuoteProject, message: string) => void;
  onFail: (reason: PublicQuoteFailReason) => void;
}) {
  const { api } = useSession();
  const [disputing, setDisputing] = useState(false);
  const [reason, setReason] = useState('');
  const [nudge, setNudge] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const now = new Date().toISOString();

  const confirm = useMutation(async () => {
    const res = await api.confirmHomeownerProject(token);
    if (res.ok) onAnswered({ ...project, status: 'completed_confirmed', confirmed_at: now, confirmed_by: 'homeowner' }, 'Thanks for confirming.');
    else onFail(failReasonOf(res.reason) ?? 'not_found');
    return res;
  });
  const dispute = useMutation(async () => {
    const res = await api.disputeHomeownerProject(token, reason.trim());
    if (res.ok) onAnswered({ ...project, status: 'disputed', disputed_at: now }, 'Thanks for telling us.');
    else onFail(failReasonOf(res.reason) ?? 'not_found');
    return res;
  });
  const error = confirm.error ?? dispute.error;
  const errorReason = failFromError(error);
  useEffect(() => {
    if (errorReason) onFail(errorReason);
  }, [errorReason, onFail]);
  const pending = confirm.pending || dispute.pending;

  const summary = (
    <section className="panel panel-pad stack-sm" aria-labelledby="job-summary">
      <h2 id="job-summary" className="small muted" style={{ margin: 0 }}>
        The job
      </h2>
      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{quote.scope}</p>
      <p className="small muted" style={{ margin: 0 }}>
        Quoted at {money(quote, project.contract_value_cents || quote.amount_cents)}
      </p>
    </section>
  );

  if (project.status !== 'completed_reported') {
    const confirmedBody =
      project.confirmed_by === 'auto'
        ? `It was confirmed automatically${project.confirmed_at ? ` on ${formatDate(project.confirmed_at)}` : ''} because we didn’t hear back. If something isn’t right, contact ${contractor.name}.`
        : project.confirmed_by === 'operator'
          ? `FinderlyFix confirmed it${project.confirmed_at ? ` on ${formatDate(project.confirmed_at)}` : ''}. If something isn’t right, contact ${contractor.name}.`
          : `Thanks for confirming${project.confirmed_at ? ` on ${formatDate(project.confirmed_at)}` : ''}. If something comes up later, contact ${contractor.name}.`;
    const copy: Record<Exclude<PublicQuoteProject['status'], 'completed_reported'>, { title: string; body: string }> = {
      completed_confirmed: { title: 'The job is confirmed complete', body: confirmedBody },
      disputed: {
        title: 'Thanks, we’re looking into it',
        body: `FinderlyFix is reviewing the job with ${contractor.name} and will be in touch by email.`,
      },
      accepted: {
        title: 'The job isn’t marked complete yet',
        body: `${contractor.name} hasn’t reported the job as finished. You’ll get an email when they do.`,
      },
      in_progress: {
        title: 'The job isn’t marked complete yet',
        body: `${contractor.name} hasn’t reported the job as finished. You’ll get an email when they do.`,
      },
      cancelled: {
        title: 'This job was cancelled',
        body: `If you have questions about it, contact ${contractor.name}.`,
      },
    };
    const c = copy[project.status];
    return (
      <>
        <Title titleRef={titleRef}>{c.title}</Title>
        <p className="muted">{c.body}</p>
        {summary}
      </>
    );
  }

  return (
    <>
      <Title titleRef={titleRef}>Did {contractor.name} complete the job?</Title>
      <p className="muted" style={{ margin: 0 }}>
        {contractor.name} told us the work is finished. Let us know if that’s right.
        {project.auto_confirm_at ? ` If we don’t hear from you by ${formatDate(project.auto_confirm_at)}, we’ll take it as done.` : ''}
      </p>
      {summary}
      {project.completion_note ? (
        <section className="sunk stack-xs" aria-labelledby="completion-note">
          <h2 id="completion-note" className="small" style={{ margin: 0 }}>
            {contractor.name}’s note
          </h2>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{project.completion_note}</p>
        </section>
      ) : null}
      {disputing ? (
        <section className="panel panel-pad stack" aria-labelledby="dispute-title">
          <h2 id="dispute-title">What’s wrong?</h2>
          <label className="field" htmlFor="dispute-reason">
            <span className="label">Tell FinderlyFix what happened</span>
            <textarea
              id="dispute-reason"
              ref={reasonRef}
              className="input"
              rows={4}
              maxLength={1000}
              value={reason}
              aria-invalid={nudge || undefined}
              aria-describedby={nudge ? 'dispute-nudge' : undefined}
              onChange={(e) => {
                setReason(e.target.value);
                if (e.target.value.trim()) setNudge(false);
              }}
            />
          </label>
          {nudge ? (
            <p id="dispute-nudge" className="notice bad" role="alert" style={{ margin: 0 }}>
              {NUDGE.reason}
            </p>
          ) : null}
          {errorReason ? null : <ErrorNotice error={dispute.error} />}
          <div className="actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={pending}
              onClick={() => {
                if (!reason.trim()) {
                  setNudge(true);
                  reasonRef.current?.focus();
                  return;
                }
                void dispute.run();
              }}
            >
              {dispute.pending ? 'Sending…' : 'Send to FinderlyFix'}
            </button>
            <button type="button" className="btn" disabled={pending} onClick={() => setDisputing(false)}>
              Back
            </button>
          </div>
        </section>
      ) : (
        <>
          {errorReason ? null : <ErrorNotice error={confirm.error} />}
          <div className="actions">
            <button type="button" className="btn btn-accent" disabled={pending} onClick={() => void confirm.run()}>
              {confirm.pending ? 'Sending…' : 'Yes, the job is done'}
            </button>
            <button type="button" className="btn btn-ghost" disabled={pending} onClick={() => setDisputing(true)}>
              Something’s wrong
            </button>
          </div>
        </>
      )}
    </>
  );
}
